// SystemSettings.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box, Typography, Grid, Card, CardContent, TextField, InputAdornment,
  FormControlLabel, Switch, MenuItem, Select, InputLabel, FormControl,
  Button, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, Tooltip
} from "@mui/material";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  fetchSystemSettings,
  updateSystemSettings,
  fetchGlobalSystemSettings,
  updateGlobalSystemSettings,
} from "../services/systemSettingsService";
import DividendGovernancePanel from "../components/DividendGovernancePanel";

const brand = "#800b33";
const brandHover = "#6c0a2b";

const fadeIn = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.38, ease: "easeOut" },
  }),
};

// ===== Default Settings & Permissions (sample) =====
const DEFAULT_SETTINGS = {
  interestRate: 17,
  depositFee: 10,
  bankChargePercent: 1,
  systemRegistrationFeeAmount: 5000,
  systemAnnualFeeAmount: 10000,
  systemAnnualFeeDeadlineMonth: 12,
  systemAnnualFeeDeadlineDay: 31,
  safReminderDays: "7, 3, 1",
  contributionReminderDays: "7, 3, 1",
  loanReminderDays: "7, 3, 1",
  autoDividendTarget: "ALL",
  autoDividendIncludeInterest: false,
  autoDividendIncludePenalties: false,
  autoDividendIncludeDefaults: false,
  penaltyTZS: 2000,
  contributionHandlingMode: "PAYG_NO_PREPAY",
  requiredDepositPercent: 50,
  retentionPercent: 20,
  sundayRestriction: true,
  defaultRepaymentMethod: "Daily",
  maxLoanTermMonths: 12,
  penaltyExecutionTime: "19:00",
  autoDeactivateArrears: true,
  entryFeeAmount: 0,
  statementFeePerPage: 0,
};
const METHOD_API_TO_UI = {
  DAILY: "Daily",
  WEEKLY: "Weekly",
  MONTHLY: "Monthly",
};
const METHOD_UI_TO_API = {
  Daily: "DAILY",
  Weekly: "WEEKLY",
  Monthly: "MONTHLY",
};
const CONTRIBUTION_MODE_LABELS = {
  PAYG_NO_PREPAY: "Pay-As-You-Go (No Prepay)",
  PREPAY_ADVANCE_ALLOWED: "Prepaid (Advance Covers Future Periods)",
};

const ALL_PERMISSIONS = [
  { key: "view_dashboard", label: "View Dashboard" },
  { key: "create_member", label: "Create Member" },
  { key: "edit_member", label: "Edit Member" },
  { key: "terminate_member", label: "Terminate Member" },
  { key: "apply_loan", label: "Apply Loan" },
  { key: "approve_loan", label: "Approve Loan" },
  { key: "disburse_loan", label: "Disburse Loan" },
  { key: "post_contribution", label: "Post Contribution" },
  { key: "configure_settings", label: "Configure Settings" },
  { key: "process_waiver", label: "Process Penalty Waiver" },
  { key: "generate_reports", label: "Generate Reports" },
];

const DEFAULT_ROLE_MATRIX = {
  Admin: [
    "view_dashboard", "create_member", "edit_member", "terminate_member",
    "approve_loan", "disburse_loan", "configure_settings", "generate_reports", "process_waiver"
  ],
  Chairperson: [
    "view_dashboard", "create_member", "edit_member", "terminate_member",
    "approve_loan", "post_contribution", "process_waiver", "generate_reports"
  ],
  Katibu: [
    "view_dashboard", "edit_member", "approve_loan", "post_contribution", "generate_reports", "process_waiver"
  ],
  Muhasibu: [
    "view_dashboard", "disburse_loan", "post_contribution", "generate_reports", "process_waiver"
  ],
  Member: [
    "view_dashboard", "apply_loan", "post_contribution"
  ],
};

// ===== Utility formatters =====

const fmtTZS = (n) => new Intl.NumberFormat("en-TZ").format(n);
const formatCurrencyInput = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const numericValue = Number(String(value).replace(/,/g, ""));
  if (!Number.isFinite(numericValue)) return "";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(numericValue);
};
const parseCurrencyInput = (value) => {
  const normalized = String(value ?? "")
    .replace(/,/g, "")
    .replace(/[^\d.]/g, "");
  const [whole, ...decimalParts] = normalized.split(".");
  const sanitized = decimalParts.length ? `${whole}.${decimalParts.join("")}` : whole;
  if (!sanitized || sanitized === ".") return "";
  const numericValue = Number(sanitized);
  return Number.isFinite(numericValue) ? numericValue : "";
};
const settingNumber = (value, fallback = 0) => {
  const numericValue = Number(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(numericValue) ? numericValue : fallback;
};
const formatReminderDays = (value) => {
  if (!Array.isArray(value) || !value.length) return "";
  return value.join(", ");
};
const parseReminderDays = (value, label, t) => {
  if (!String(value || "").trim()) return [];
  const days = String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => Number(item));
  if (days.some((day) => !Number.isInteger(day) || day < 0 || day > 365)) {
    throw new Error(t("{{label}} must be a comma-separated list of integers between 0 and 365.", { label }));
  }
  return Array.from(new Set(days)).sort((a, b) => b - a);
};

// Make a human-readable diff for the SweetAlert summary & audit trail
const summarizeChanges = (
  prev,
  next,
  {
    includeInterest = true,
    includeRetention = true,
    includeRequiredDeposit = true,
    includeBankCharge = false,
    includeStatementFee = true,
    includeSystemFeeFields = false,
  } = {},
  t
) => {
  const changes = [];
  if (includeInterest && prev.interestRate !== next.interestRate) changes.push(`${t("Interest Rate (%)")}: ${prev.interestRate}% → ${next.interestRate}%`);
  if (prev.depositFee !== next.depositFee) changes.push(`${t("System Service Charge (SSC %)")}: ${prev.depositFee}% → ${next.depositFee}%`);
  if (includeBankCharge && prev.bankChargePercent !== next.bankChargePercent) {
    changes.push(`${t("Bank Charge (%)")}: ${prev.bankChargePercent}% → ${next.bankChargePercent}%`);
  }
  if (includeSystemFeeFields && prev.systemRegistrationFeeAmount !== next.systemRegistrationFeeAmount) {
    changes.push(`${t("System Registration Fee (SRF)")}: ${fmtTZS(prev.systemRegistrationFeeAmount)} → ${fmtTZS(next.systemRegistrationFeeAmount)} TZS`);
  }
  if (includeSystemFeeFields && prev.systemAnnualFeeAmount !== next.systemAnnualFeeAmount) {
    changes.push(`${t("System Annual Fee (SAF)")}: ${fmtTZS(prev.systemAnnualFeeAmount)} → ${fmtTZS(next.systemAnnualFeeAmount)} TZS`);
  }
  if (
    includeSystemFeeFields &&
    (prev.systemAnnualFeeDeadlineMonth !== next.systemAnnualFeeDeadlineMonth ||
      prev.systemAnnualFeeDeadlineDay !== next.systemAnnualFeeDeadlineDay)
  ) {
    changes.push(
      `${t("SAF Payment Deadline")}: ${prev.systemAnnualFeeDeadlineMonth}/${prev.systemAnnualFeeDeadlineDay} → ${next.systemAnnualFeeDeadlineMonth}/${next.systemAnnualFeeDeadlineDay}`
    );
  }
  if (includeSystemFeeFields && prev.safReminderDays !== next.safReminderDays) {
    changes.push(`${t("SAF Reminder Days")}: ${prev.safReminderDays} → ${next.safReminderDays}`);
  }
  if (includeSystemFeeFields && prev.contributionReminderDays !== next.contributionReminderDays) {
    changes.push(`${t("Contribution Reminder Days")}: ${prev.contributionReminderDays} → ${next.contributionReminderDays}`);
  }
  if (includeSystemFeeFields && prev.loanReminderDays !== next.loanReminderDays) {
    changes.push(`${t("Loan Reminder Days")}: ${prev.loanReminderDays} → ${next.loanReminderDays}`);
  }
  if (prev.penaltyTZS !== next.penaltyTZS) changes.push(`${t("Penalty")}: ${fmtTZS(prev.penaltyTZS)} → ${fmtTZS(next.penaltyTZS)} TZS`);
  if (prev.autoDividendTarget !== next.autoDividendTarget) changes.push(`${t("Dividend Distribution Target")}: ${prev.autoDividendTarget} → ${next.autoDividendTarget}`);
  if (prev.autoDividendIncludeInterest !== next.autoDividendIncludeInterest) changes.push(`${t("Auto Dividend from Interest")}: ${prev.autoDividendIncludeInterest ? "ON" : "OFF"} → ${next.autoDividendIncludeInterest ? "ON" : "OFF"}`);
  if (prev.autoDividendIncludePenalties !== next.autoDividendIncludePenalties) changes.push(`${t("Auto Dividend from Penalties")}: ${prev.autoDividendIncludePenalties ? "ON" : "OFF"} → ${next.autoDividendIncludePenalties ? "ON" : "OFF"}`);
  if (prev.autoDividendIncludeDefaults !== next.autoDividendIncludeDefaults) changes.push(`${t("Auto Dividend from Defaults")}: ${prev.autoDividendIncludeDefaults ? "ON" : "OFF"} → ${next.autoDividendIncludeDefaults ? "ON" : "OFF"}`);
  if (prev.contributionHandlingMode !== next.contributionHandlingMode) {
    changes.push(`${t("Contribution Handling Mode")}: ${CONTRIBUTION_MODE_LABELS[prev.contributionHandlingMode] || prev.contributionHandlingMode} → ${CONTRIBUTION_MODE_LABELS[next.contributionHandlingMode] || next.contributionHandlingMode}`);
  }
  if (includeRetention && prev.retentionPercent !== next.retentionPercent) changes.push(`${t("Retention Percent (%)")}: ${prev.retentionPercent}% → ${next.retentionPercent}%`);
  if (includeRequiredDeposit && prev.requiredDepositPercent !== next.requiredDepositPercent) {
    changes.push(`${t("Required Deposit (%)")}: ${prev.requiredDepositPercent}% → ${next.requiredDepositPercent}%`);
  }
  if (prev.sundayRestriction !== next.sundayRestriction) changes.push(`${t("Restrict Sunday transactions (create/repayment not allowed)")}: ${prev.sundayRestriction ? "ON" : "OFF"} → ${next.sundayRestriction ? "ON" : "OFF"}`);
  if (prev.defaultRepaymentMethod !== next.defaultRepaymentMethod) changes.push(`${t("Default Repayment Method")}: ${prev.defaultRepaymentMethod} → ${next.defaultRepaymentMethod}`);
  if (prev.maxLoanTermMonths !== next.maxLoanTermMonths) changes.push(`${t("Max Loan Term (Months)")}: ${prev.maxLoanTermMonths} → ${next.maxLoanTermMonths}`);
  if (prev.penaltyExecutionTime !== next.penaltyExecutionTime) changes.push(`${t("Penalty Execution Time")}: ${prev.penaltyExecutionTime} → ${next.penaltyExecutionTime}`);
  if (prev.autoDeactivateArrears !== next.autoDeactivateArrears) changes.push(`${t("Auto-deactivate members in arrears")}: ${prev.autoDeactivateArrears ? "ON" : "OFF"} → ${next.autoDeactivateArrears ? "ON" : "OFF"}`);
  if (prev.entryFeeAmount !== next.entryFeeAmount) changes.push(`${t("Entry Fee Amount")}: ${fmtTZS(prev.entryFeeAmount)} → ${fmtTZS(next.entryFeeAmount)}`);
  if (includeStatementFee && prev.statementFeePerPage !== next.statementFeePerPage) changes.push(`${t("Statement Fee Per Page")}: ${fmtTZS(prev.statementFeePerPage)} → ${fmtTZS(next.statementFeePerPage)}`);
  return changes;
};

const BaseSystemSettings = ({
  title = "System Settings",
  subtitle = "Configure core financial parameters. All changes are logged.",
  fetchSettingsApi,
  updateSettingsApi,
  allowDepositEdit,
  allowRetentionEdit = false,
  showInterestField = true,
  showRetentionField = true,
  showDepositField = true,
  showRequiredDepositField = false,
  showBankChargeField = false,
  showPenaltyField = true,
  showContributionModeField = true,
  showRepaymentField = true,
  showSundayRestriction = true,
  showStatementFeeField = false,
  showEntryFeeField = true,
  showAutoDeactivateField = true,
  showPenaltyTimeField = true,
  showSystemFeeFields = false,
  showDividendGovernance = false,
}) => {
  const { t } = useTranslation();
  // Settings state
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [savedSettings, setSavedSettings] = useState(DEFAULT_SETTINGS);

  // Permissions state
  const [roleMatrix, setRoleMatrix] = useState(DEFAULT_ROLE_MATRIX);
  const [savedRoleMatrix, setSavedRoleMatrix] = useState(DEFAULT_ROLE_MATRIX);

  // Audit log (from API)
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const mapSettingsFromApi = (payload) => ({
    interestRate: payload.interest_rate !== undefined ? Number(payload.interest_rate) : DEFAULT_SETTINGS.interestRate,
    depositFee: payload.deposit_fee_percent !== undefined ? Number(payload.deposit_fee_percent) : DEFAULT_SETTINGS.depositFee,
    bankChargePercent: payload.bank_charge_percent !== undefined ? Number(payload.bank_charge_percent) : DEFAULT_SETTINGS.bankChargePercent,
    systemRegistrationFeeAmount: payload.system_registration_fee_amount !== undefined ? Number(payload.system_registration_fee_amount) : DEFAULT_SETTINGS.systemRegistrationFeeAmount,
    systemAnnualFeeAmount: payload.system_annual_fee_amount !== undefined ? Number(payload.system_annual_fee_amount) : DEFAULT_SETTINGS.systemAnnualFeeAmount,
    systemAnnualFeeDeadlineMonth: payload.system_annual_fee_deadline_month !== undefined ? Number(payload.system_annual_fee_deadline_month) : DEFAULT_SETTINGS.systemAnnualFeeDeadlineMonth,
    systemAnnualFeeDeadlineDay: payload.system_annual_fee_deadline_day !== undefined ? Number(payload.system_annual_fee_deadline_day) : DEFAULT_SETTINGS.systemAnnualFeeDeadlineDay,
    safReminderDays: payload.saf_reminder_days !== undefined ? formatReminderDays(payload.saf_reminder_days) : DEFAULT_SETTINGS.safReminderDays,
    contributionReminderDays: payload.contribution_reminder_days !== undefined ? formatReminderDays(payload.contribution_reminder_days) : DEFAULT_SETTINGS.contributionReminderDays,
    loanReminderDays: payload.loan_reminder_days !== undefined ? formatReminderDays(payload.loan_reminder_days) : DEFAULT_SETTINGS.loanReminderDays,
    penaltyTZS: payload.penalty_amount !== undefined ? Number(payload.penalty_amount) : DEFAULT_SETTINGS.penaltyTZS,
    contributionHandlingMode: payload.contribution_handling_mode || DEFAULT_SETTINGS.contributionHandlingMode,
    requiredDepositPercent: payload.required_deposit_percent !== undefined ? Number(payload.required_deposit_percent) : DEFAULT_SETTINGS.requiredDepositPercent,
    retentionPercent: payload.withdrawal_retention_percent !== undefined ? Number(payload.withdrawal_retention_percent) : DEFAULT_SETTINGS.retentionPercent,
    sundayRestriction: payload.restrict_sunday_operations !== undefined ? Boolean(payload.restrict_sunday_operations) : DEFAULT_SETTINGS.sundayRestriction,
    defaultRepaymentMethod: METHOD_API_TO_UI[payload.default_repayment_method] || DEFAULT_SETTINGS.defaultRepaymentMethod,
    maxLoanTermMonths: payload.max_loan_term_months !== undefined ? Number(payload.max_loan_term_months) : DEFAULT_SETTINGS.maxLoanTermMonths,
    penaltyExecutionTime: payload.penalty_execution_time ? String(payload.penalty_execution_time).slice(0, 5) : DEFAULT_SETTINGS.penaltyExecutionTime,
    autoDeactivateArrears: payload.auto_deactivate_arrears !== undefined ? Boolean(payload.auto_deactivate_arrears) : DEFAULT_SETTINGS.autoDeactivateArrears,
    entryFeeAmount: payload.entry_fee_amount !== undefined ? Number(payload.entry_fee_amount) : DEFAULT_SETTINGS.entryFeeAmount,
    statementFeePerPage: payload.statement_fee_per_page !== undefined ? Number(payload.statement_fee_per_page) : DEFAULT_SETTINGS.statementFeePerPage,
    autoDividendTarget: payload.dividend_distribution_target || DEFAULT_SETTINGS.autoDividendTarget,
    autoDividendIncludeInterest: payload.auto_dividend_include_interest !== undefined ? Boolean(payload.auto_dividend_include_interest) : DEFAULT_SETTINGS.autoDividendIncludeInterest,
    autoDividendIncludePenalties: payload.auto_dividend_include_penalties !== undefined ? Boolean(payload.auto_dividend_include_penalties) : DEFAULT_SETTINGS.autoDividendIncludePenalties,
    autoDividendIncludeDefaults: payload.auto_dividend_include_defaults !== undefined ? Boolean(payload.auto_dividend_include_defaults) : DEFAULT_SETTINGS.autoDividendIncludeDefaults,
  });

  const mapAuditLogs = (logs = []) =>
    logs.map((log) => ({
      when: new Date(log.created_at).toLocaleString(),
      who: log.changed_by_name,
      changes:
        log.changes?.map((c) => `${c.label || c.field}: ${c.from} → ${c.to}`).join("; ") ||
        "No field changes",
    }));

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const payload = await fetchSettingsApi();
        const mapped = mapSettingsFromApi(payload);
        setSettings(mapped);
        setSavedSettings(mapped);
        setAudit(mapAuditLogs(payload.recent_logs));
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: t("Failed to load settings"),
          text: error.message,
          confirmButtonColor: brand,
        });
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, []);

  // Derived helpers
  const dirty = useMemo(() => {
    return JSON.stringify(settings) !== JSON.stringify(savedSettings) ||
      JSON.stringify(roleMatrix) !== JSON.stringify(savedRoleMatrix);
  }, [settings, roleMatrix, savedSettings, savedRoleMatrix]);

  // Toggle role permission
  const togglePermission = (role, permKey) => {
    setRoleMatrix((prev) => {
      const has = prev[role].includes(permKey);
      const next = has ? prev[role].filter((p) => p !== permKey) : [...prev[role], permKey];
      return { ...prev, [role]: next };
    });
  };

  // Set all permissions for a role (quick actions)
  const setAllForRole = (role, on) => {
    setRoleMatrix((prev) => ({
      ...prev,
      [role]: on ? ALL_PERMISSIONS.map((p) => p.key) : []
    }));
  };

  // Set recommended defaults for a role
  const setRecommended = (role) => {
    setRoleMatrix((prev) => ({ ...prev, [role]: DEFAULT_ROLE_MATRIX[role] ?? [] }));
  };

  const handleSave = async () => {
    // Validation (simple examples)
    if (showInterestField && (settings.interestRate < 0 || settings.interestRate > 100)) {
      Swal.fire({ icon: "warning", title: t("Invalid Interest Rate"), text: t("Interest must be between 0% and 100%.") });
      return;
    }
    if (allowDepositEdit && (settings.depositFee < 0 || settings.depositFee > 100)) {
      Swal.fire({ icon: "warning", title: t("Invalid SSC%"), text: t("SSC must be between 0% and 100%.") });
      return;
    }
    if (showPenaltyField && settingNumber(settings.penaltyTZS) < 0) {
      Swal.fire({ icon: "warning", title: t("Invalid Penalty"), text: t("Penalty amounts cannot be negative.") });
      return;
    }
    if (!Number.isInteger(Number(settings.maxLoanTermMonths)) || Number(settings.maxLoanTermMonths) < 1 || Number(settings.maxLoanTermMonths) > 120) {
      Swal.fire({ icon: "warning", title: t("Invalid Loan Term"), text: t("Max loan term must be between 1 and 120 months.") });
      return;
    }
    if (showRetentionField && allowRetentionEdit && (settings.retentionPercent < 0 || settings.retentionPercent > 100)) {
      Swal.fire({ icon: "warning", title: t("Invalid Retention %"), text: t("Retention must be between 0% and 100%.") });
      return;
    }
    if (showRequiredDepositField && (settings.requiredDepositPercent < 0 || settings.requiredDepositPercent > 100)) {
      Swal.fire({ icon: "warning", title: t("Invalid Required Deposit %"), text: t("Required deposit must be between 0% and 100%.") });
      return;
    }

    if (showBankChargeField && (settings.bankChargePercent < 0 || settings.bankChargePercent > 100)) {
      Swal.fire({ icon: "warning", title: t("Invalid Bank Charge %"), text: t("Bank charge must be between 0% and 100%.") });
      return;
    }
    if (showSystemFeeFields && settingNumber(settings.systemRegistrationFeeAmount) < 0) {
      Swal.fire({ icon: "warning", title: t("Invalid SRF Amount"), text: t("System Registration Fee cannot be negative.") });
      return;
    }
    if (showSystemFeeFields && settingNumber(settings.systemAnnualFeeAmount) < 0) {
      Swal.fire({ icon: "warning", title: t("Invalid SAF Amount"), text: t("System Annual Fee cannot be negative.") });
      return;
    }
    if (
      showSystemFeeFields &&
      (!Number.isInteger(Number(settings.systemAnnualFeeDeadlineMonth)) ||
        Number(settings.systemAnnualFeeDeadlineMonth) < 1 ||
        Number(settings.systemAnnualFeeDeadlineMonth) > 12)
    ) {
      Swal.fire({ icon: "warning", title: t("Invalid SAF Deadline Month"), text: t("SAF deadline month must be between 1 and 12.") });
      return;
    }
    if (
      showSystemFeeFields &&
      (!Number.isInteger(Number(settings.systemAnnualFeeDeadlineDay)) ||
        Number(settings.systemAnnualFeeDeadlineDay) < 1 ||
        Number(settings.systemAnnualFeeDeadlineDay) > 31)
    ) {
      Swal.fire({ icon: "warning", title: t("Invalid SAF Deadline Day"), text: t("SAF deadline day must be between 1 and 31.") });
      return;
    }


let safReminderDays = [];
let contributionReminderDays = [];
let loanReminderDays = [];
if (showSystemFeeFields) {
  try {
    safReminderDays = parseReminderDays(settings.safReminderDays, t("SAF Reminder Days"), t);
    contributionReminderDays = parseReminderDays(settings.contributionReminderDays, t("Contribution Reminder Days"), t);
    loanReminderDays = parseReminderDays(settings.loanReminderDays, t("Loan Reminder Days"), t);
  } catch (error) {
    Swal.fire({ icon: "warning", title: t("Invalid Reminder Days"), text: error.message });
    return;
  }
}

// Summaries
    const settingsDiff = summarizeChanges(savedSettings, settings, {
      includeInterest: showInterestField,
      includeRetention: showRetentionField,
      includeRequiredDeposit: showRequiredDepositField,
      includeBankCharge: showBankChargeField,
      includeStatementFee: showStatementFeeField,
      includeSystemFeeFields: showSystemFeeFields,
    }, t);
    const roleChanges = JSON.stringify(savedRoleMatrix) !== JSON.stringify(roleMatrix)
      ? ["Role permissions updated"]
      : [];

    const allChanges = [...settingsDiff, ...roleChanges];
    const htmlSummary = allChanges.length
      ? `<ul style="text-align:left;margin:0;padding-left:18px">${allChanges.map(c => `<li>${c}</li>`).join("")}</ul>`
      : t("No changes detected.");

    const payload = {};
    if (showDepositField && allowDepositEdit) {
      payload.deposit_fee_percent = settings.depositFee;
    }
    if (showPenaltyField) {
      payload.penalty_amount = settingNumber(settings.penaltyTZS);
    }
    if (showContributionModeField) {
      payload.contribution_handling_mode = settings.contributionHandlingMode;
    }
    if (showBankChargeField) {
      payload.bank_charge_percent = settings.bankChargePercent;
    }
    if (showSundayRestriction) {
      payload.restrict_sunday_operations = settings.sundayRestriction;
    }
    if (showRepaymentField) {
      payload.default_repayment_method = METHOD_UI_TO_API[settings.defaultRepaymentMethod] || "DAILY";
      payload.max_loan_term_months = Number(settings.maxLoanTermMonths);
    }
    if (showInterestField) {
      payload.interest_rate = settings.interestRate;
    }
    if (showRetentionField) {
      payload.withdrawal_retention_percent = settings.retentionPercent;
    }
    if (showRequiredDepositField) {
      payload.required_deposit_percent = settings.requiredDepositPercent;
    }
    if (showPenaltyTimeField) {
      payload.penalty_execution_time = settings.penaltyExecutionTime ? `${settings.penaltyExecutionTime}:00` : "19:00:00";
    }
    if (showAutoDeactivateField) {
      payload.auto_deactivate_arrears = settings.autoDeactivateArrears;
    }
    if (showEntryFeeField) {
      payload.entry_fee_amount = settingNumber(settings.entryFeeAmount);
    }
    if (showDividendGovernance) {
      payload.dividend_distribution_target = settings.autoDividendTarget;
      payload.auto_dividend_include_interest = settings.autoDividendIncludeInterest;
      payload.auto_dividend_include_penalties = settings.autoDividendIncludePenalties;
      payload.auto_dividend_include_defaults = settings.autoDividendIncludeDefaults;
    }
    if (showStatementFeeField) {
      payload.statement_fee_per_page = settingNumber(settings.statementFeePerPage);
    }
    if (showSystemFeeFields) {
      payload.system_registration_fee_amount = settingNumber(settings.systemRegistrationFeeAmount);
      payload.system_annual_fee_amount = settingNumber(settings.systemAnnualFeeAmount);
      payload.system_annual_fee_deadline_month = Number(settings.systemAnnualFeeDeadlineMonth);
      payload.system_annual_fee_deadline_day = Number(settings.systemAnnualFeeDeadlineDay);
      payload.saf_reminder_days = safReminderDays;
      payload.contribution_reminder_days = contributionReminderDays;
      payload.loan_reminder_days = loanReminderDays;
    }

    try {
      setSaving(true);
      const response = await updateSettingsApi(payload);
      const updated = mapSettingsFromApi(response);
      setSettings(updated);
      setSavedSettings(updated);
      setSavedRoleMatrix(roleMatrix);
      setAudit(mapAuditLogs(response.recent_logs));
      Swal.fire({
        icon: "success",
        title: t("Settings Saved"),
        html: htmlSummary,
        confirmButtonColor: brand,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: t("Save failed"),
        text: error.message,
        confirmButtonColor: brand,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    Swal.fire({
      icon: "warning",
      title: t("Reset to Defaults?"),
      text: t("This will revert settings and permissions to default recommendations."),
      showCancelButton: true,
      confirmButtonColor: brand,
      confirmButtonText: t("Reset"),
    }).then((res) => {
      if (res.isConfirmed) {
        setSettings(DEFAULT_SETTINGS);
        setRoleMatrix(DEFAULT_ROLE_MATRIX);
        Swal.fire({ icon: "success", title: t("Reset Complete"), confirmButtonColor: brand });
      }
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="text.secondary">{t("Loading system settings...")}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 2, display: "flex", alignItems: "baseline", gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
          {t(title)}
        </Typography>
        <Chip label={dirty ? t("Unsaved changes") : t("Up to date")} size="small" color={dirty ? "warning" : "success"} />
      </Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 3 }}>
        {t(subtitle)}
      </Typography>

      <Grid container spacing={3}>
        {/* ===== Financial Parameters ===== */}
        <Grid item xs={12} lg={7}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {t("Financial Parameters")}
                  </Typography>
                  <Tooltip title={t("Interest applies to loans; SSC applies only to deposit transactions; penalties are posted daily at 19:01.")}>
                    <InfoOutlinedIcon fontSize="small" />
                  </Tooltip>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  {showInterestField && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t("Interest Rate (%)")}
                        type="number"
                        value={settings.interestRate}
                        onChange={(e) => setSettings((s) => ({ ...s, interestRate: Number(e.target.value) }))}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      />
                    </Grid>
                  )}

                  {showDepositField && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t("System Service Charge (SSC %)")}
                        type="number"
                        value={settings.depositFee}
                        onChange={(e) => setSettings((s) => ({ ...s, depositFee: Number(e.target.value) }))}
                        disabled={!allowDepositEdit}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        helperText={
                          allowDepositEdit
                            ? t("Applies globally to deposit transactions only. Default is 10%.")
                            : t("Managed centrally by the system administrator. Default is 10%.")
                        }
                      />
                    </Grid>
                  )}

                  {showBankChargeField && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t("Bank Charge (%)")}
                        type="number"
                        value={settings.bankChargePercent}
                        onChange={(e) => setSettings((s) => ({ ...s, bankChargePercent: Number(e.target.value) }))}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        helperText={t("Percent deducted from dividend pools for bank / processing charges.")}
                      />
                    </Grid>
                  )}

                  {showSystemFeeFields && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t("System Registration Fee (SRF)")}
                        value={formatCurrencyInput(settings.systemRegistrationFeeAmount)}
                        onChange={(e) => setSettings((s) => ({ ...s, systemRegistrationFeeAmount: parseCurrencyInput(e.target.value) }))}
                        InputProps={{ startAdornment: <InputAdornment position="start">TZS</InputAdornment> }}
                        inputProps={{ inputMode: "decimal" }}
                        helperText={t("Applies to newly created members only. Default is 5,000 TZS.")}
                      />
                    </Grid>
                  )}

                  {showSystemFeeFields && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t("System Annual Fee (SAF)")}
                        value={formatCurrencyInput(settings.systemAnnualFeeAmount)}
                        onChange={(e) => setSettings((s) => ({ ...s, systemAnnualFeeAmount: parseCurrencyInput(e.target.value) }))}
                        InputProps={{ startAdornment: <InputAdornment position="start">TZS</InputAdornment> }}
                        inputProps={{ inputMode: "decimal" }}
                        helperText={t("Yearly access fee. Default is 10,000 TZS.")}
                      />
                    </Grid>
                  )}

                  {showSystemFeeFields && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t("SAF Deadline Month")}
                        type="number"
                        value={settings.systemAnnualFeeDeadlineMonth}
                        onChange={(e) => setSettings((s) => ({ ...s, systemAnnualFeeDeadlineMonth: Number(e.target.value) }))}
                        inputProps={{ min: 1, max: 12 }}
                        helperText={t("Calendar month used for the annual SAF deadline.")}
                      />
                    </Grid>
                  )}

                  {showSystemFeeFields && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t("SAF Deadline Day")}
                        type="number"
                        value={settings.systemAnnualFeeDeadlineDay}
                        onChange={(e) => setSettings((s) => ({ ...s, systemAnnualFeeDeadlineDay: Number(e.target.value) }))}
                        inputProps={{ min: 1, max: 31 }}
                        helperText={t("Calendar day used for the annual SAF deadline.")}
                      />
                    </Grid>
                  )}

                  {showSystemFeeFields && (
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label={t("SAF Reminder Days")}
                        value={settings.safReminderDays}
                        onChange={(e) => setSettings((s) => ({ ...s, safReminderDays: e.target.value }))}
                        helperText={t("Comma-separated reminder days before SAF deadline. Example: 7, 3, 1.")}
                      />
                    </Grid>
                  )}

                  {showSystemFeeFields && (
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label={t("Contribution Reminder Days")}
                        value={settings.contributionReminderDays}
                        onChange={(e) => setSettings((s) => ({ ...s, contributionReminderDays: e.target.value }))}
                        helperText={t("Comma-separated reminder days before contribution due dates. Example: 7, 3, 1.")}
                      />
                    </Grid>
                  )}

                  {showSystemFeeFields && (
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label={t("Loan Reminder Days")}
                        value={settings.loanReminderDays}
                        onChange={(e) => setSettings((s) => ({ ...s, loanReminderDays: e.target.value }))}
                        helperText={t("Comma-separated reminder days before loan due dates. Example: 7, 3, 1.")}
                      />
                    </Grid>
                  )}

                  {showRetentionField && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t("Retention Percent (%)")}
                        type="number"
                        value={settings.retentionPercent}
                        onChange={(e) => setSettings((s) => ({ ...s, retentionPercent: Number(e.target.value) }))}
                        disabled={!allowRetentionEdit}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        helperText={
                          allowRetentionEdit
                            ? t("Percent retained when members withdraw.")
                            : t("Managed centrally by the platform admin.")
                        }
                      />
                    </Grid>
                  )}

                  {showRequiredDepositField && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t("Required Deposit (%)")}
                        type="number"
                        value={settings.requiredDepositPercent}
                        onChange={(e) => setSettings((s) => ({ ...s, requiredDepositPercent: Number(e.target.value) }))}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        helperText={t("Minimum savings percentage before a member can borrow.")}
                      />
                    </Grid>
                  )}

                  {showPenaltyField && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t("Penalty Amount (TZS)")}
                        fullWidth
                        value={formatCurrencyInput(settings.penaltyTZS)}
                        onChange={(e) => setSettings((s) => ({ ...s, penaltyTZS: parseCurrencyInput(e.target.value) }))}
                        InputProps={{ startAdornment: <InputAdornment position="start">TZS</InputAdornment> }}
                        inputProps={{ inputMode: "decimal" }}
                        helperText={t("Applied uniformly regardless of repayment schedule.")}
                      />
                    </Grid>
                  )}
                  {showContributionModeField && (
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>{t("Contribution Handling Mode")}</InputLabel>
                        <Select
                          label={t("Contribution Handling Mode")}
                          value={settings.contributionHandlingMode}
                          onChange={(e) => setSettings((s) => ({ ...s, contributionHandlingMode: e.target.value }))}
                        >
                          {Object.entries(CONTRIBUTION_MODE_LABELS).map(([value, label]) => (
                            <MenuItem key={value} value={value}>
                              {t(label)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  {showPenaltyTimeField && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t("Penalty Execution Time")}
                        type="time"
                        value={settings.penaltyExecutionTime}
                        onChange={(e) => setSettings((s) => ({ ...s, penaltyExecutionTime: e.target.value }))}
                        helperText={t("Daily cutoff for applying late penalties (24h).")}
                      />
                    </Grid>
                  )}
                  {showAutoDeactivateField && (
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.autoDeactivateArrears}
                            onChange={(e) => setSettings((s) => ({ ...s, autoDeactivateArrears: e.target.checked }))}
                          />
                        }
                        label={t("Auto-deactivate members in arrears")}
                      />
                    </Grid>
                  )}
                  {showEntryFeeField && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t("Entry Fee Amount")}
                        value={formatCurrencyInput(settings.entryFeeAmount)}
                        onChange={(e) => setSettings((s) => ({ ...s, entryFeeAmount: parseCurrencyInput(e.target.value) }))}
                        InputProps={{ startAdornment: <InputAdornment position="start">TZS</InputAdornment> }}
                        inputProps={{ inputMode: "decimal" }}
                        helperText={t("One-time fee per member when joining.")}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t("Max Loan Term (Months)")}
                      type="number"
                      value={settings.maxLoanTermMonths}
                      onChange={(e) => setSettings((s) => ({ ...s, maxLoanTermMonths: Number(e.target.value) }))}
                      inputProps={{ min: 1, max: 120 }}
                      helperText={t("Maximum loan term allowed for this group.")}
                    />
                  </Grid>
                  {showStatementFeeField && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t("Statement Fee Per Page")}
                        value={formatCurrencyInput(settings.statementFeePerPage)}
                        onChange={(e) => setSettings((s) => ({ ...s, statementFeePerPage: parseCurrencyInput(e.target.value) }))}
                        InputProps={{ startAdornment: <InputAdornment position="start">TZS</InputAdornment> }}
                        inputProps={{ inputMode: "decimal" }}
                        helperText={t("Billable fee per page when downloading member statements.")}
                      />
                    </Grid>
                  )}

                  {showRepaymentField && (
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>{t("Default Repayment Method")}</InputLabel>
                        <Select
                          label={t("Default Repayment Method")}
                          value={settings.defaultRepaymentMethod}
                          onChange={(e) => setSettings((s) => ({ ...s, defaultRepaymentMethod: e.target.value }))}
                        >
                          <MenuItem value="Daily">{t("Daily")}</MenuItem>
                          <MenuItem value="Weekly">{t("Weekly")}</MenuItem>
                          <MenuItem value="Monthly">{t("Monthly")}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}

                  {showSundayRestriction && (
                    <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center" }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.sundayRestriction}
                            onChange={(e) => setSettings((s) => ({ ...s, sundayRestriction: e.target.checked }))}
                          />
                        }
                        label={t("Restrict Sunday transactions (create/repayment not allowed)")}
                      />
                    </Grid>
                  )}
                </Grid>

                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={!dirty || saving}
                    sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
                  >
                    {saving ? t("Saving...") : t("Save Settings")}
                  </Button>
                  <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={handleReset}>
                    {t("Reset to Defaults")}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </motion.div>

          <Box sx={{ mt: 3 }}>
            <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={2}>
              <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                    {t("Configuration Audit Trail")}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <TableContainer component={Paper} elevation={0}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>Timestamp</TableCell>
                          <TableCell>Actor</TableCell>
                          <TableCell>Changes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {audit.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                              {t("No audit entries yet.")}
                            </TableCell>
                          </TableRow>
                        ) : (
                          audit.map((a, i) => (
                            <TableRow key={`${a.when}-${i}`} hover>
                              <TableCell>{i + 1}</TableCell>
                              <TableCell>{a.when}</TableCell>
                              <TableCell>
                                <Chip label={a.who} size="small" color={a.who === "Admin" ? "error" : "primary"} />
                              </TableCell>
                              <TableCell>{a.changes || "-"}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </motion.div>
          </Box>
        </Grid>


        <Grid item xs={12} lg={5}>
          {showDividendGovernance && (
            <DividendGovernancePanel
              settings={settings}
              onChange={(patch) => setSettings((current) => ({ ...current, ...patch }))}
            />
          )}
        </Grid>
      </Grid>

      {/* Footer */}
      <Typography variant="caption" sx={{ display: "block", textAlign: "center", color: "text.secondary", mt: 4 }}>
        © {new Date().getFullYear()} Savvy Community • Settings are logged for compliance
      </Typography>
    </Box>
  );
};

const GroupSystemSettings = () => (
  <BaseSystemSettings
    title="Group System Settings"
    subtitle="These settings affect only your group, including automatic dividend distribution rules."
    fetchSettingsApi={fetchSystemSettings}
    updateSettingsApi={updateSystemSettings}
    allowDepositEdit={false}
    allowRetentionEdit={true}
    showInterestField={true}
    showRetentionField={true}
    showDepositField={false}
    showRequiredDepositField={true}
    showContributionModeField={true}
    showDividendGovernance={true}
  />
);

export const AdminSystemSettings = () => (
  <BaseSystemSettings
    title="Platform System Settings"
    subtitle="Manage platform-wide defaults for SSC, SRF, SAF, payment deadlines, and notification reminder schedules."
    fetchSettingsApi={fetchGlobalSystemSettings}
    updateSettingsApi={updateGlobalSystemSettings}
    allowDepositEdit={true}
    allowRetentionEdit={false}
    showInterestField={false}
    showRetentionField={false}
    showBankChargeField={true}
    showPenaltyField={false}
    showRepaymentField={false}
    showSundayRestriction={false}
    showStatementFeeField={true}
    showEntryFeeField={false}
    showAutoDeactivateField={false}
    showPenaltyTimeField={false}
    showContributionModeField={false}
    showSystemFeeFields={true}
  />
);

export default GroupSystemSettings;
